import { Box } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import { useSystemDetailsTabs } from 'src/Libs/service-dashboard/hooks/use-system-details-tabs'
import { ApiView } from '@e3dc-react/shell/Elements/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ErrorLogDto } from '../dto/error-logs.dto'
import { useErrorLogsTableData } from '../hooks'
import PaperContainer from '../components/PaperContainer'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'

const useStyles = makeStyles()(theme => ({
  container: {
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  searchFieldContainer: {
    marginBottom: theme.spacing(2),
    background: theme.palette.shades.shade04,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  statusFilter: {
    minWidth: `10%`,
    marginLeft: 0,
  },
  occurranceFilter: {
    minWidth: 'fit-content',
    marginLeft: theme.spacing(1),
  },
}))

/**
 * This component represents the error logs table for the selected system
 * @returns Error logs Component
 **/
const ErrorLogs: FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { columns, fetchProps } = useErrorLogsTableData(storageId ?? '')
  const [filterQuery, setFilterQuery] = useState('')
  const [showSolved, setShowSolved] = useState<boolean>(true)

  const { setActiveTab } = useSystemDetailsTabs(installationId, storageId)

  useEffect(() => {
    setActiveTab(2)
  }, []) //eslint-disable-line

  return (
    <Box className={classes.container}>
      <BannerQueue location={BannerLocations.SystemDetails} />
      <PaperContainer title={t('system-details.error-logs.title')}>
        <ApiView<ErrorLogDto>
          fetchProps={fetchProps}
          tableColumns={columns}
          bodyElevation={0}
          footerElevation={0}
          headerElevation={0}
          initialTablePageLimit={25}
          oneFilter={{
            label: t('common.search'),
            filterQuery,
            setFilterQuery,
            properties: ['id', 'source', 'message', 'errorType'],
            maxLength: 100,
            minLength: 3,
          }}
          filters={[
            {
              label: t('system-details.error-logs.filters.status-selector.title'),
              name: 'cleared',
              customOnChange: value => {
                setShowSolved(value === 'true')
              },
              className: classes.statusFilter,
              type: {
                type: 'select',
                options: [
                  {
                    label: t('system-details.error-logs.filters.status-selector.solved'),
                    value: 'true',
                  },
                  {
                    label: t('system-details.error-logs.filters.status-selector.unsolved'),
                    value: 'false',
                  },
                ],
                value: `${showSolved}`,
                searchable: false,
              },
            },
            {
              label: t('system-details.error-logs.table.first-occur'),
              name: 'firstOccur',
              className: classes.occurranceFilter,
              type: {
                type: 'dateRange',
              },
            },
            {
              label: t('system-details.error-logs.table.last-occur'),
              name: 'lastOccur',
              className: classes.occurranceFilter,
              type: {
                type: 'dateRange',
              },
            },
          ]}
          customPayload={{
            getCleared: showSolved,
          }}
          filterClassName={classes.searchFieldContainer}
          onlyTable
          hasColoredTableColumns
        />
      </PaperContainer>
    </Box>
  )
}

export default ErrorLogs
